import React from 'react'
import { Link } from 'react-router-dom'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import { 
  BookOpen, 
  Target, 
  Clock, 
  Users, 
  ArrowRight,
  Trophy,
  TrendingUp,
  GraduationCap
} from 'lucide-react'

const DanhSachLop: React.FC = () => {
  const lopData = [
    {
      id: '10',
      title: 'Lớp 10',
      description: 'Cơ học - Động học và Động lực học',
      color: 'from-sky-50 to-sky-100',
      bgColor: 'from-blue-50 to-blue-100',
      iconColor: 'text-blue-600',
      stats: {
        chapters: 4,
        lessons: 20,
        topics: 'Động học, Động lực học, Năng lượng, Chuyển động tròn'
      }
    },
    {
      id: '11',
      title: 'Lớp 11',
      description: 'Dao động - Sóng - Điện học',
      color: 'from-emerald-50 to-emerald-100',
      bgColor: 'from-green-50 to-green-100',
      iconColor: 'text-green-600',
      stats: {
        chapters: 4,
        lessons: 18,
        topics: 'Dao động, Sóng, Điện trường, Dòng điện'
      }
    },
    {
      id: '12',
      title: 'Lớp 12',
      description: 'Vật lí hiện đại - Hạt nhân',
      color: 'from-purple-50 to-purple-100',
      bgColor: 'from-purple-50 to-purple-100',
      iconColor: 'text-purple-600',
      stats: {
        chapters: 4,
        lessons: 22,
        topics: 'Vật lí nhiệt, Khí lí tưởng, Từ trường, Vật lí hạt nhân'
      }
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-800 text-sm font-medium rounded-full mb-8">
            <GraduationCap className="w-4 h-4 mr-2" />
            Chọn lớp học phù hợp
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-slate-700 mb-6">
            Chọn Lớp Học
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
            Khám phá chương trình Vật lí từ Lớp 10 đến Lớp 12 với nội dung tương tác và mô phỏng sinh động
          </p>
        </div>

        {/* Classes Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
          {lopData.map((lop) => (
            <div 
              key={lop.id}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${lop.color} p-8 transition-all duration-300 transform hover:scale-105 hover:shadow-xl group`}
            >
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                <div className={`w-full h-full bg-gradient-to-br ${lop.bgColor} rounded-full transform translate-x-8 -translate-y-8`}></div>
              </div>
              
              <div className="relative z-10">
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-white/50 mb-6 ${lop.iconColor}`}>
                  <BookOpen className="w-8 h-8" />
                </div>
                
                {/* Title */}
                <h3 className="text-2xl font-bold text-slate-700 mb-3">
                  {lop.title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {lop.description}
                </p>
                
                {/* Stats */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-center text-sm text-gray-600">
                    <Target className="w-4 h-4 mr-2" />
                    {lop.stats.chapters} chương
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <BookOpen className="w-4 h-4 mr-2" />
                    {lop.stats.lessons} bài học
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    {lop.stats.topics}
                  </div>
                </div>
                
                {/* Action Button */}
                <Link 
                  to={`/lop/${lop.id}`}
                  className="inline-flex items-center justify-center w-full px-6 py-3 bg-white/80 hover:bg-white text-slate-700 font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg group-hover:shadow-xl"
                >
                  Khám phá {lop.title}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-700 mb-6 text-center">
            Tại sao chọn Ebook Vật lí tương tác?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-100 text-blue-600 mb-4">
                <Trophy className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-slate-700 mb-2">Nội dung chất lượng</h3>
              <p className="text-sm text-gray-600">Chương trình chuẩn Bộ Giáo dục với mô phỏng 3D sinh động</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-green-100 text-green-600 mb-4">
                <Target className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-slate-700 mb-2">Tương tác thông minh</h3>
              <p className="text-sm text-gray-600">Học tập qua thí nghiệm ảo và trắc nghiệm tự động</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-purple-100 text-purple-600 mb-4">
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-slate-700 mb-2">Học mọi lúc mọi nơi</h3>
              <p className="text-sm text-gray-600">Truy cập 24/7 trên mọi thiết bị, không cần internet</p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  )
}

export default DanhSachLop
